import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-4 py-12 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 mb-8"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">Effective Date: April 2, 2026</p>

        <div className="prose prose-gray max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              iHireFast ("we", "us", "our") is committed to protecting the privacy of our users
              and their applicants. This Privacy Policy explains what data we collect, how we use
              it, how we store it, and your rights regarding your personal information. This
              policy applies to all users of the iHireFast platform, including recruiters,
              hiring managers, and job applicants who interact with forms or messages sent through
              the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Data We Collect</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We collect the following categories of personal information:
            </p>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Account Information</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
              <li>Full name</li>
              <li>Email address</li>
              <li>Password (encrypted)</li>
              <li>Company name</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Applicant Data</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-1 mb-4">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Resume / CV files</li>
              <li>Job application details (job title, source, status)</li>
              <li>SMS message content and history</li>
              <li>Interview scheduling information</li>
              <li>Form submission data (custom fields submitted through application forms)</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mb-2">Usage Data</h3>
            <ul className="list-disc list-inside text-gray-600 space-y-1">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Pages visited and actions taken within the Service</li>
              <li>Timestamps of access and activity</li>
              <li>Device information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Data</h2>
            <p className="text-gray-600 leading-relaxed mb-3">We use collected data to:</p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>Provide, maintain, and improve the iHireFast platform</li>
              <li>Facilitate SMS communication between recruiters and applicants</li>
              <li>Manage applicant tracking and interview scheduling</li>
              <li>Process and store resumes and application form submissions</li>
              <li>Generate analytics and reporting for workspace dashboards</li>
              <li>Send account-related notifications and service updates</li>
              <li>Ensure security and prevent fraud or abuse</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Data Storage & Security</h2>
            <p className="text-gray-600 leading-relaxed">
              Your data is stored on Cloudflare infrastructure, including Cloudflare Workers,
              Cloudflare D1 (database), and Cloudflare R2 (file storage). Cloudflare provides
              enterprise-grade security, encryption in transit (TLS), and data centers distributed
              globally for reliability and performance. Resumes and uploaded files are stored in
              Cloudflare R2 with access controls to ensure only authorized users can retrieve
              them. We implement industry-standard security measures including encrypted
              passwords, secure API authentication, and role-based access controls within
              workspaces.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Sharing</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We do not sell your personal data. We may share data with the following parties
              only as necessary to provide the Service:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>
                <strong>SMS Providers:</strong> Phone numbers and message content are transmitted
                through SMS service providers to deliver text messages
              </li>
              <li>
                <strong>Scheduling Services:</strong> Name, email, and scheduling details may be
                shared with Calendly when scheduling interviews
              </li>
              <li>
                <strong>Job Boards:</strong> We receive applicant data from Indeed through
                webhook integrations configured by you
              </li>
              <li>
                <strong>Workspace Members:</strong> Data within a workspace is accessible to all
                members of that workspace based on their role permissions
              </li>
              <li>
                <strong>Legal Requirements:</strong> We may disclose data if required by law,
                regulation, or legal process
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              Depending on your jurisdiction, you may have the following rights:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal retention requirements</li>
              <li><strong>Portability:</strong> Request your data in a structured, machine-readable format</li>
              <li><strong>Objection:</strong> Object to processing of your data for certain purposes</li>
              <li><strong>Withdrawal of Consent:</strong> Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:privacy@ihirefast.com" className="text-primary-600 hover:text-primary-700 underline">
                privacy@ihirefast.com
              </a>.
              We will respond to requests within 30 days.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Cookies & Tracking</h2>
            <p className="text-gray-600 leading-relaxed mb-3">
              We use cookies and similar technologies for the following purposes:
            </p>
            <ul className="list-disc list-inside text-gray-600 space-y-2">
              <li>
                <strong>Essential Cookies:</strong> Required for authentication, session management,
                and core functionality of the Service
              </li>
              <li>
                <strong>Analytics Cookies:</strong> Help us understand how users interact with the
                Service so we can improve it
              </li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">
              We do not use advertising or third-party tracking cookies. You can manage cookie
              preferences through your browser settings, though disabling essential cookies may
              affect functionality.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your account data for as long as your account is active. Applicant data
              is retained for the duration of your subscription plus 30 days after account
              termination. You may request deletion of specific applicant records at any time
              through the dashboard or by contacting us. Backups may retain data for up to 90
              days after deletion.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              The Service is not intended for individuals under 18 years of age. We do not
              knowingly collect personal information from children. If we become aware that we
              have collected data from a child under 18, we will promptly delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of material
              changes by email or by posting a notice on the Service. Your continued use of the
              Service after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have questions or concerns about this Privacy Policy or our data practices,
              please contact us at:
            </p>
            <div className="mt-3 p-4 bg-gray-50 rounded-lg text-gray-600">
              <p className="font-medium text-gray-900">iHireFast</p>
              <p>Email:{' '}
                <a href="mailto:privacy@ihirefast.com" className="text-primary-600 hover:text-primary-700 underline">
                  privacy@ihirefast.com
                </a>
              </p>
              <p>Support:{' '}
                <a href="mailto:support@ihirefast.com" className="text-primary-600 hover:text-primary-700 underline">
                  support@ihirefast.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
